<template>
  <div class="clinic-voucher container">
    <div class="d-flex justify-content-between align-items-center my-8">
      <div>
        <h4 class="text-dark fw-bold mb-1">Quản lý Voucher</h4>
        <p class="text-muted m-0">Tạo và quản lý mã giảm giá cho khách hàng</p>
      </div>
      <div class="d-flex gap-3">
        <button v-if="isViewPermission" class="btn btn-outline-success font-weight-medium mr-2">
          <i class="fas fa-download mr-2"></i>Xuất báo cáo
        </button>
        <button v-if="isInsertPermission" class="btn btn-success font-weight-medium" @click="$router.push({ name: 'clinic_voucher_create' })">
          <i class="fas fa-plus mr-2"></i>Tạo voucher mới
        </button>
      </div>
    </div>
    <div class="filter-bar bg-white rounded border mb-6">
      <div class="row align-items-center">
        <div class="col-md-2 filter-col-input">
          <div class="position-relative">
            <i class="fas fa-search search-icon"></i>
            <input
              v-model="filters.search"
              type="text"
              class="form-control search-input"
              placeholder="Tìm kiếm theo mã hoặc tên..."
              @input="handleSearch"
            />
          </div>
        </div>

        <div class="col-md-2">
          <basic-select
            label=""
            placeholder="Tất cả trạng thái"
            name="voucherStatus"
            :options="voucherStatusOptions"
            track-by="value"
            value-label="text" 
            :value.sync="voucherStatusValue"
            @input="filters.status = $event != null ? $event : ''; handleSearch()"
          />
        </div>

        <div class="col-md-2">
          <basic-select
            label=""
            placeholder="Tất cả loại"
            name="discountType"
            :options="discountTypeOptions"
            track-by="value"
            value-label="text"
            :value.sync="discountTypeValue"
            @input="filters.discountType = $event != null ? $event : ''; handleSearch()"
          />
        </div>

        <div class="col-md-2">
          <basic-date-picker
            :value.sync="filters.dateFrom"
            label=""
            placeholder="Từ ngày"
            name="dateFrom"
            format="DD/MM/YYYY"
            @input="handleSearch"
          />
        </div>

        <div class="col-md-2">
          <basic-date-picker
            :value.sync="filters.dateTo"
            label=""
            placeholder="Đến ngày"
            name="dateTo"
            format="DD/MM/YYYY"
            @input="handleSearch"
          />
        </div>

         <div class="col-md-2 filter-col-input d-flex align-items-center">
        <button class="btn btn-secondary btn-reset-filter w-100" @click="resetFilters">
          Reset bộ lọc
        </button>
      </div>
      </div>
    </div>

    <div class="row mb-5">
      <div class="col-md-3">
        <div class="summary-card">
          <div class="summary-card__label">Tổng voucher</div>
          <div class="summary-card__value text-dark">{{ stats.total }}</div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="summary-card">
          <div class="summary-card__label">Đang hoạt động</div>
          <div class="summary-card__value text-success">{{ stats.active }}</div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="summary-card">
          <div class="summary-card__label">Đã sử dụng</div>
          <div class="summary-card__value text-primary">{{ stats.used }}</div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="summary-card">
          <div class="summary-card__label">Hết hạn</div>
          <div class="summary-card__value text-danger">{{ stats.expired }}</div>
        </div>
      </div>
    </div>

    <div class="mb-4">
      <span class="text-muted">Hiển thị {{ stats.total }} voucher</span>
    </div>

    <b-overlay :show="loading">
      <div class="row">
        <div
          v-for="(voucher, index) in vouchers"
          :key="voucher.id || index"
          class="col-xl-3 col-lg-3 col-md-4 mb-4"
        >
          <div class="voucher-card h-100">

            <div class="d-flex align-items-center mb-3">
              <span :class="statusBadgeClass(voucher)" class="status-badge mr-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-ticket-perforated-fill mr-1" viewBox="0 0 16 16">
                  <path d="M0 4.5A1.5 1.5 0 0 1 1.5 3h13A1.5 1.5 0 0 1 16 4.5V6a.5.5 0 0 1-.5.5 1.5 1.5 0 0 0 0 3 .5.5 0 0 1 .5.5v1.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 11.5V10a.5.5 0 0 1 .5-.5 1.5 1.5 0 1 0 0-3A.5.5 0 0 1 0 6zm4 1a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 0-1h-7A.5.5 0 0 0 4 5.5m0 5a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 0-1h-7a.5.5 0 0 0-.5.5M4 8a1 1 0 0 0 1 1h6a1 1 0 1 0 0-2H5a1 1 0 0 0-1 1"/>
                </svg>
                {{ statusLabel(voucher) }}
              </span>
            </div>

            <div class="voucher-code-row mb-3">
              <div class="voucher-code">{{ voucher.code }}</div>
              <button class="copy-btn" @click="copyCode(voucher.code)" :title="'Sao chép ' + voucher.code">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" class="bi bi-copy" viewBox="0 0 16 16">
                  <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
                </svg>
              </button>
            </div>

            <div class="mb-3">
              <div class="voucher-name">{{ voucher.title }}</div>
              <div class="voucher-type text-muted">{{ voucher.discountType ? 'Cố định' : 'Phần trăm' }}</div>
            </div>

            <div class="discount-box mb-4">
              <div class="discount-box__label">Giảm giá</div>
              <div class="discount-box__value">
                <template v-if="voucher.discountType">
                  {{ formatPrice(voucher.discountValue) }} VNĐ
                </template>
                <template v-else>
                  {{ voucher.discountValue }}%
                  <span v-if="voucher.maxDiscountAmount" class="discount-box__cap"> (Tối đa: {{ formatPrice(voucher.maxDiscountAmount) }}VND)</span>
                </template>
              </div>
            </div>

            <div class="voucher-meta mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span class="text-muted">Đơn tối thiểu:</span>
                <span class="font-weight-medium">{{ formatPrice(voucher.minOrderAmount) }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">Đã dùng:</span>
                <span class="font-weight-medium">{{voucher.usedCount}}/{{ voucher.usageLimit }}</span>
              </div>
              <div class="progress-bar-wrapper">
                <div
                  class="progress-bar-fill"
                  :style="{ width: usagePercent(voucher) + '%' }"
                  :class="'bg-success'"
                ></div>
              </div>
            </div>

            <!-- Date range -->
            <div class="voucher-dates mb-4 text-muted">
              {{ formatDate(voucher.startDate) }} – {{ formatDate(voucher.endDate) }}
            </div>

            <!-- Action button -->
            <button v-if="isUpdatePermission" class="btn btn-primary w-100" @click="$router.push({ name: 'clinic_voucher_edit', params: { id: voucher.id } })">
              <i class="far fa-edit mr-2"></i>Chỉnh sửa
            </button>

          </div>
        </div>

      <div v-if="paging.total === 0 && !loading" class="col-12 text-center py-12">
          <i class="fas fa-ticket-alt fa-3x text-muted mb-4 d-block"></i>
          <p class="text-muted">Chưa có voucher nào. Hãy tạo voucher mới!</p>
        </div>
      </div>

      <div v-if="paging.total > 0" class="d-flex justify-content-between align-items-center mt-4">
        <div class="d-flex align-items-center">
          <b-form-select
            v-model="paging.pageSize"
            :options="pageSizeOptions"
            size="sm"
            style="width: 80px;"
            class="page-size-select"
          />
        </div>
        <b-pagination
          v-model="paging.page"
          :total-rows="paging.total"
          :per-page="paging.pageSize"
          size="md"
          class="mb-0 pagination-custom"
          first-number
          last-number
        />
      </div>
    </b-overlay>



  </div>
</template>

<script>
export default {
  name: 'ClinicVoucher',
  data() {
    return {
      loading: false,
      debounceTimer: null,
      vouchers: [],
      filters: {
        search: '',
        status: '',
        discountType: '',
        dateFrom: '',
        dateTo: '',
      },
      voucherStatusOptions: [
        { value: true, text: 'Hoạt động' },
        { value: false, text: 'Không hoạt động' },
      ],
      discountTypeOptions: [
        { value: 'false', text: 'Phần trăm' },
        { value: 'true', text: 'Cố định' },
      ],

      stats: {
        total: 0,
        active: 0,
        used: 0,
        expired: 0,
      },
      paging: {
        page: 1,
        pageSize: 12,
        total: 0,
      },
      pageSizeOptions: [
        { value: 6,  text: '6' },
        { value: 12, text: '12' },
        { value: 24, text: '24' },
      ],
    };
  },

  computed: {
    voucherStatusValue: {
      get() {
        return this.voucherStatusOptions.find((o) => o.value === this.filters.status) || null;
      },
      set(obj) {
        this.filters.status = obj?.value ?? '';
      },
    },
    discountTypeValue: {
      get() {
        return this.discountTypeOptions.find((o) => o.value === this.filters.discountType) || null;
      },
      set(obj) {
        this.filters.discountType = obj?.value ?? '';
      },
    },
  },
  watch: {
    'paging.page'()     { this.loadVouchers(); },
    'paging.pageSize'() { this.paging.page = 1; this.loadVouchers(); },
  },

  mounted() {
    this.loadVouchers();
    this.loadStats();
  },
  activated() {
    this.loadVouchers();
    this.loadStats();
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },

  methods: {
    handleSearch() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.paging.page = 1;
        this.loadVouchers();
      }, 2000);
    },

    resetFilters() {
      this.filters = {
        search: '',
        status: '',
        discountType: '',
        dateFrom: '',
        dateTo: '',
      };
      this.paging.page = 1;
      this.loadVouchers();
    },

    async loadVouchers() {
      try {
        this.loading = true;
        const params = new URLSearchParams();
        params.append('page', this.paging.page);
        params.append('size', this.paging.pageSize);
        if (this.filters.search)   params.append('code',   this.filters.search);
        if (this.filters.status !== '')   params.append('status',   this.filters.status);
        if (this.filters.discountType !== '')     params.append('discountType',     this.filters.discountType);
        if (this.filters.dateFrom) {
          const d = this.parsePickerDate(this.filters.dateFrom);
          if (d) params.append('startDate', Math.floor(d.getTime() / 1000));
        }
        if (this.filters.dateTo) {
          const d = this.parsePickerDate(this.filters.dateTo, { endOfDay: true });
          if (d) params.append('endDate', Math.floor(d.getTime() / 1000));
        }

        const res = await this.$api.get('/BundleVouchers', { params });
        const raw = res?.data ?? res;

        if (Array.isArray(raw)) {
          this.vouchers = raw;
          this.paging.total = raw.length;
        } else if (Array.isArray(raw?.items)) {
          this.vouchers = raw.items;
          this.paging.total = raw.total ?? raw.items.length;
        } else if (Array.isArray(raw?.data)) {
          this.vouchers = raw.data;
          this.paging.total = raw.total ?? raw.data.length;
        } else {
          this.vouchers = [];
          this.paging.total = 0;
        }

        // stats được load riêng qua loadStats()
      } catch (e) {
        this.$toastr?.e({ title: 'Lỗi', msg: 'Không thể tải danh sách voucher' });
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        const res = await this.$api.get('/BundleVouchers/VoucherStatistics');
        const raw = res?.data ?? res;
        const data = raw?.data ?? raw;
        this.stats = {
          total:   data?.item1 ?? 0,
          active:  data?.item2 ?? 0,
          used:    data?.item3 ?? 0,
          expired: data?.item4 ?? 0,
        };
      } catch (e) {
        console.error('Không thể tải thống kê voucher:', e);
      }
    },

    statusLabel(voucher) {
      const nowSeconds = Math.floor(Date.now() / 1000);
      if (voucher.endDate && voucher.endDate < nowSeconds) return 'Hết hạn';
      if (voucher.status === true || voucher.status === 0) return 'Hoạt động';
      if (voucher.status === false || voucher.status === 1) return 'Không hoạt động';
      return 'Không xác định';
    },

    statusBadgeClass(voucher) {
      const nowSeconds = Math.floor(Date.now() / 1000);
      if (voucher.endDate && voucher.endDate < nowSeconds) return 'badge-expired';
      if (voucher.status === true || voucher.status === 0) return 'badge-active';
      return 'badge-inactive';
    },

    formatPrice(val) {
      if (!val && val !== 0) return '0';
      return new Intl.NumberFormat('vi-VN').format(val);
    },

    formatDate(val) {
      if (!val) return '?';
      // API returns seconds, convert to milliseconds
      const ms = typeof val === 'number' && val < 1e10 ? val * 1000 : val;
      return this.$moment(ms).format('D/M/YYYY');
    },

    usagePercent(voucher) {
      const used = voucher.usedCount || 0;
      const limit = voucher.usageLimit || 0;
      if (limit <= 0) return 0;
      return Math.min(100, Math.round((used / limit) * 100));
    },

    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code);
        this.$toastr?.s({ title: 'Đã sao chép', msg: code });
      } catch {
        /* fallback */
      }
    },

    parseAnyDate(val) {
      if (!val) return null;
      if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;
      if (typeof val === 'number') {
        const ms = val < 1e10 ? val * 1000 : val;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      if (typeof val === 'string') {
        const m = this.$moment(val, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
        if (m.isValid()) return m.toDate();
        const d = new Date(val);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      return null;
    },

    parsePickerDate(val, { endOfDay = false } = {}) {
      if (!val) return null;
      const d = this.parseAnyDate(val);
      if (!d) return null;
      const out = new Date(d);
      if (endOfDay) out.setHours(23, 59, 59, 999);
      else out.setHours(0, 0, 0, 0);
      return out;
    },
  },
};
</script>

<style scoped lang="scss">
.clinic-voucher {
  padding: 0;
}

/* Filter bar */
.filter-bar {
  padding: 16px 20px;

  .row {
    row-gap: 10px;
  }

  ::v-deep .form-group {
    margin-bottom: 0;
    label {
      display: none;
    }
  }

  .filter-col-input {
    display: flex;
    align-items: center;
  }
}

.search-input {
  padding-left: 38px;
  border-radius: 8px;
  border: 1px solid #e1e5e9;
  height: 42px;
}
.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a1a5b7;
  z-index: 5;
  font-size: 0.85rem;
}
.filter-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a1a5b7;
  z-index: 5;
  font-size: 0.82rem;
}
.filter-select {
  height: 42px;
  border-radius: 8px;
  border: 1px solid #e1e5e9;
  font-size: 0.9rem;
}
.pl-icon { padding-left: 34px; }

/* Summary cards */
.summary-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  padding: 18px 22px;

  &__label {
    font-size: 0.85rem;
    color: #7e8299;
    margin-bottom: 6px;
  }
  &__value {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1;
  }
}

/* Voucher card */
.voucher-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 14px;
  padding: 20px 22px;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(76, 87, 125, 0.12);
  }
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.78rem;
  font-weight: 600;
}
.badge-active   { background: #e8fff3; color: #50cd89; }
.badge-inactive { background: #f3f4f8; color: #7e8299; }
.badge-expired  { background: #fff5f8; color: #f1416c; }

/* Code row */
.voucher-code-row {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  padding: 10px 14px;
  gap: 8px;
}
.voucher-code {
  flex: 1;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #fff;
}
.copy-btn {
  background: rgba(255,255,255,0.25);
  border: none;
  border-radius: 6px;
  padding: 5px 8px;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
  line-height: 1;

  &:hover { background: rgba(255,255,255,0.4); }
}

/* Name / type */
.voucher-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #181c32;
}
.voucher-type {
  font-size: 0.82rem;
}

.discount-box {
  background: #f0fff8;
  border-radius: 8px;
  padding: 12px 14px;

  &__label {
    font-size: 0.78rem;
    color: #a1a5b7;
    margin-bottom: 4px;
    font-weight: 500;
  }
  &__value {
    font-size: 1.6rem;
    font-weight: 700;
    color: #17c653;
    line-height: 1.2;
  }
  &__cap {
    font-size: 0.8rem;
    color: #7e8299;
    font-weight: 400;
  }
}

/* Meta */
.voucher-meta {
  font-size: 0.88rem;
}

/* Progress bar */
.progress-bar-wrapper {
  height: 5px;
  background: #e4e6ef;
  border-radius: 4px;
  overflow: hidden;
}
.progress-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.4s ease;
}

.voucher-dates {
  font-size: 0.82rem;
  margin-top: auto;
}

.page-size-select {
  border-color: #dee2e6;
  font-size: 0.875rem;
}
::v-deep .pagination-custom {
  .page-item {
    &.active .page-link {
      background-color: #01645A;
      border-color: #01645A;
      color: #fff;
    }
    .page-link {
      color: #21a567;
      &:hover {
        background-color: #e8f8f0;
        border-color: #21a567;
        color: #21a567;
      }
    }
  }
}

</style>